import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Check,
  Zap,
  Building,
  Rocket,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { usePlans } from "@/hooks/use-plans";
import { formatCurrency, formatNumber } from "@/lib/utils";
import { Plan } from "@/types";
import { Link } from "@tanstack/react-router";

export const Route = createFileRoute("/docs/pricing")({
  component: PricingPage,
});

// Fallback plans for when API is unavailable
const fallbackPlans = [] as Plan[];

function PricingPage() {
  const { plans: apiPlans, loading, error } = usePlans();

  // Use API plans if available, otherwise fallback to static plans
  const plans = apiPlans.length > 0 ? apiPlans : fallbackPlans;

  // Helper function to get plan icon
  const getPlanIcon = (planName: string) => {
    const name = planName.toLowerCase();
    if (name.includes("free")) return Zap;
    if (name.includes("researcher") || name.includes("individual"))
      return Building;
    if (name.includes("project") || name.includes("team")) return Rocket;
    return Building;
  };

  // Helper function to get plan credits
  const getPlanCredits = (plan: Plan): number => {
    return (
      plan.credits ||
      (plan.metadata?.credits ? parseInt(plan.metadata.credits) : 0) ||
      (plan.metadata?.credit ? parseInt(plan.metadata.credit) : 0) ||
      1000
    );
  };

  // Helper function to format interval
  const formatInterval = (interval: string): string => {
    switch (interval) {
      case "month":
        return "month";
      case "year":
        return "year";
      default:
        return interval;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose the plan that fits your needs. All plans include our core
            features with no hidden fees.
          </p>
        </div>

        {loading ? (
          <Card className="mb-12">
            <CardContent className="flex items-center justify-center py-12">
              <div className="flex items-center space-x-2">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span>Loading pricing plans...</span>
              </div>
            </CardContent>
          </Card>
        ) : error ? (
          <Card className="mb-12">
            <CardContent className="flex items-center justify-center py-12">
              <div className="text-center space-y-4">
                <AlertCircle className="w-12 h-12 text-amber-500 mx-auto" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Using Fallback Pricing
                  </h3>
                  <p className="text-gray-500 mb-4">
                    Showing standard pricing. For the most up-to-date plans,
                    please visit your dashboard.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : null}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {plans
            .filter((plan) => plan.active)
            .map((plan) => {
              const Icon = getPlanIcon(plan.name);
              const planCredits = getPlanCredits(plan);
              const isPopular =
                plan.name.toLowerCase().includes("project") ||
                plan.name.toLowerCase().includes("pro");

              return (
                <Card
                  key={plan.id}
                  className={`relative ${isPopular ? "ring-2 ring-blue-500" : ""}`}
                >
                  {isPopular && (
                    <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-500">
                      Most Popular
                    </Badge>
                  )}
                  <CardHeader className="text-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <CardTitle>{plan.name}</CardTitle>
                    <div className="text-3xl font-bold">
                      {(plan.price || 0) === 0
                        ? "Free"
                        : formatCurrency(plan.price || 0, plan.currency)}
                      {plan.price && plan.price > 0 && (
                        <span className="text-lg font-normal text-gray-600">
                          /{formatInterval(plan.interval || "month")}
                        </span>
                      )}
                    </div>
                    <CardDescription>{plan.description}</CardDescription>
                    {planCredits > 0 && (
                      <div className="text-sm text-blue-600 font-medium">
                        {formatNumber(planCredits)} credits included
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 mb-6">
                      {plan.features && plan.features.length > 0 ? (
                        plan.features.map((feature, index) => (
                          <li key={index} className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                            <span className="text-sm">
                              {typeof feature === "string"
                                ? feature
                                : feature.name || "Feature"}
                            </span>
                          </li>
                        ))
                      ) : (
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span className="text-sm">Basic API access</span>
                        </li>
                      )}
                    </ul>
                    <Button
                      className="w-full"
                      variant={isPopular ? "default" : "outline"}
                      asChild
                    >
                      <Link to="/dashboard/billing">
                        {(plan.price || 0) === 0
                          ? "Get Started"
                          : "Start Free Trial"}
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}

          {/* Enterprise Plan */}
          <Card className="relative">
            <CardHeader className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Building className="w-6 h-6 text-purple-600" />
              </div>
              <CardTitle>Enterprise</CardTitle>
              <div className="text-3xl font-bold">Custom</div>
              <CardDescription>For large organizations</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span className="text-sm">Unlimited API requests</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span className="text-sm">Dedicated support</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span className="text-sm">Custom SLA</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span className="text-sm">On-premise deployment</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span className="text-sm">Advanced security</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span className="text-sm">Custom billing</span>
                </li>
              </ul>
              <Button className="w-full" variant="outline">
                Contact Sales
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <Card>
            <CardHeader>
              <CardTitle>Pay-as-you-go Option</CardTitle>
              <CardDescription>
                Flexible pricing for variable usage patterns
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span>API Requests</span>
                  <span className="font-medium">$0.01 per 100 requests</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Minimum charge</span>
                  <span className="font-medium">$1.00/month</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Billing cycle</span>
                  <span className="font-medium">Monthly</span>
                </div>
              </div>
              <Button className="w-full mt-6" variant="outline">
                Enable Pay-as-you-go
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Enterprise Features</CardTitle>
              <CardDescription>
                Additional features available for enterprise customers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm">Dedicated account manager</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm">Custom rate limits and quotas</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm">Advanced security features</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm">Priority technical support</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm">Custom billing and invoicing</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="text-center">
            <CardTitle>Frequently Asked Questions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium mb-2">
                  Can I change plans anytime?
                </h3>
                <p className="text-sm text-gray-600">
                  Yes, you can upgrade or downgrade your plan at any time.
                  Changes take effect immediately.
                </p>
              </div>
              <div>
                <h3 className="font-medium mb-2">
                  What happens if I exceed my limit?
                </h3>
                <p className="text-sm text-gray-600">
                  Your requests will be rate-limited. You can upgrade your plan
                  or enable pay-as-you-go billing.
                </p>
              </div>
              <div>
                <h3 className="font-medium mb-2">Do you offer refunds?</h3>
                <p className="text-sm text-gray-600">
                  We offer a 30-day money-back guarantee for all paid plans.
                </p>
              </div>
              <div>
                <h3 className="font-medium mb-2">Is there a free trial?</h3>
                <p className="text-sm text-gray-600">
                  Yes, all paid plans include a 14-day free trial with full
                  access to features.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
